import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PropTypes from "prop-types";

import styled, { ThemeContext, ThemeProvider } from "styled-components";

import Cell from "./Cell";

import { CrosswordContext, CrosswordSizeContext } from "./context";

const defaultTheme = {
	columnBreakpoint: "768px",
	gridBackground: "rgb(0,0,0)",
	cellBackground: "rgb(255,255,255)",
	cellBorder: "rgb(0,0,0)",
	textColor: "rgb(0,0,0)",
	numberColor: "rgba(0,0,0, 0.25)",
	focusBackground: "rgb(255,255,0)",
	highlightBackground: "rgb(255,255,204)",
};

const GridWrapper = styled.div.attrs((/* props */) => ({
	className: "crossword grid",
}))`
	width: auto;
	flex: 2 1 50%;
	align-self: flex-start;

	.grid-ui {
		padding: 0;
		width: 70%;
		margin: 0px auto;
		margin-top: 20px;
		position: relative;
		line-height: 0;
		overflow: hidden;
	}

	svg {
		display: block;
		width: 100%;
		height: auto;
		vertical-align: top;
	}

	.crossword-input {
		position: absolute;
		opacity: 0;
		caret-color: transparent;
		background: transparent;
		border: 0;
		margin: 0;
		padding: 0;
		outline: none;
		/* Keep focus target inside the grid so the page does not scroll/grow */
		pointer-events: none;
		font-size: 16px;
	}
`;

const CrosswordGridPropTypes = {
	/** presentation values for the crossword; these override any values coming from a parent ThemeProvider context. */
	theme: PropTypes.shape({
		/** browser-width at which the clues go from showing beneath the grid to showing beside the grid */
		columnBreakpoint: PropTypes.string,

		/** overall background color (fill) for the crossword grid; can be `'transparent'` to show through a page background image */
		gridBackground: PropTypes.string,
		/**  background for an answer cell */
		cellBackground: PropTypes.string,
		/** border for an answer cell */
		cellBorder: PropTypes.string,
		/** color for answer text (entered by the player) */
		textColor: PropTypes.string,
		/** color for the across/down numbers in the grid */
		numberColor: PropTypes.string,
		/** background color for the cell with focus, the one that the player is typing into */
		focusBackground: PropTypes.string,
		/** background color for the cells in the answer the player is working on,
		 * helps indicate in which direction focus will be moving; also used as a
		 * background on the active clue  */
		highlightBackground: PropTypes.string,
	}),
};

/**
 * The rendering component for the crossword grid itself.
 */
export default function CrosswordGrid({ theme }) {
	const {
		size,
		gridData,
		handleInputKeyDown,
		handleInputChange,
		handleCellClick,
		handleInputClick,
		registerFocusHandler,
		focused,
		selectedPosition: { row: focusedRow, col: focusedCol },
		selectedDirection: currentDirection,
		selectedNumber: currentNumber,
	} = useContext(CrosswordContext);

	const inputRef = useRef(null);
	const contextTheme = useContext(ThemeContext);

	const focus = useCallback(() => {
		if (inputRef.current?.focus) {
			try {
				inputRef.current.focus({ preventScroll: true });
			} catch (e) {
				inputRef.current.focus();
			}
		}
	}, []);

	const [gridBounds, setGridBounds] = useState({
		usedRows: 1,
		usedCols: 1,
		viewBoxWidth: 1,
		viewBoxHeight: 1,
	});

	const cellSize = size >= 12 ? 100 / 15 : 100 / Math.max(size, 1);

	useEffect(() => {
		registerFocusHandler(focus);

		let maxCol = -1;
		let maxRow = -1;
		for (let i = 0; i < gridData.length; i++) {
			const rowData = gridData[i];
			for (let j = 0; j < rowData.length; j++) {
				const cell = rowData[j];
				if (cell?.used && (cell.guess || cell.answer)) {
					if (cell.col > maxCol) maxCol = cell.col;
					if (cell.row > maxRow) maxRow = cell.row;
				}
			}
		}

		const usedRows = Math.max(maxRow + 1, 1);
		const usedCols = Math.max(maxCol + 1, 1);

		setGridBounds({
			usedRows,
			usedCols,
			viewBoxWidth: cellSize * usedCols,
			viewBoxHeight: cellSize * usedRows,
		});

		return () => {
			registerFocusHandler(null);
		};
	}, [focus, registerFocusHandler, gridData, cellSize]);

	const cellPadding = 0.225;
	const cellInner = cellSize - cellPadding * 2;
	const cellHalf = cellSize / 2;
	const fontSize = cellInner * 0.7;

	const sizeContext = useMemo(
		() => ({ cellSize, cellPadding, cellInner, cellHalf, fontSize }),
		[cellSize, cellPadding, cellInner, cellHalf, fontSize]
	);

	// Position input as % of the *visible* used grid (not the full square size),
	// so focus never lands below the SVG and the page does not grow white space.
	const inputStyle = useMemo(() => {
		const { usedRows, usedCols } = gridBounds;
		const safeRow = Math.min(Math.max(focusedRow, 0), usedRows - 1);
		const safeCol = Math.min(Math.max(focusedCol, 0), usedCols - 1);
		const cellWidthPct = 100 / usedCols;
		const cellHeightPct = 100 / usedRows;

		return {
			top: `${safeRow * cellHeightPct}%`,
			left: `${safeCol * cellWidthPct}%`,
			width: `${cellWidthPct}%`,
			height: `${cellHeightPct}%`,
		};
	}, [gridBounds, focusedRow, focusedCol]);

	const finalTheme = useMemo(() => ({ ...defaultTheme, ...contextTheme, ...theme }), [contextTheme, theme]);

	const onCellClick = useCallback(
		(cellData) => {
			handleCellClick(cellData);
			// Focus after click without scrolling the page
			requestAnimationFrame(() => focus());
		},
		[handleCellClick, focus]
	);

	return (
		<CrosswordSizeContext.Provider value={sizeContext}>
			<ThemeProvider theme={finalTheme}>
				<GridWrapper>
					<div className="grid-ui">
						<svg
							viewBox={`0 0 ${gridBounds.viewBoxWidth} ${gridBounds.viewBoxHeight}`}
							preserveAspectRatio="xMinYMin meet"
							width="100%"
							style={{ display: "block", width: "100%", height: "auto" }}
						>
							{gridData.flatMap((rowData, row) =>
								rowData.map((cellData, col) =>
									cellData.used ? (
										<Cell
											// eslint-disable-next-line react/no-array-index-key
											key={`R${row}C${col}`}
											cellData={cellData}
											focus={focused && row === focusedRow && col === focusedCol}
											highlight={focused && !!currentNumber && cellData[currentDirection] === currentNumber}
											onClick={onCellClick}
										/>
									) : undefined
								)
							)}
						</svg>
						<input
							ref={inputRef}
							className="crossword-input"
							disabled={gridData.length ? gridData[focusedRow]?.[focusedCol]?.corrected && gridData[focusedRow]?.[focusedCol]?.completed : false}
							aria-label="crossword-input"
							type="text"
							onClick={handleInputClick}
							onKeyDown={handleInputKeyDown}
							onChange={handleInputChange}
							value=""
							autoComplete="off"
							spellCheck="false"
							autoCorrect="off"
							style={inputStyle}
						/>
					</div>
				</GridWrapper>
			</ThemeProvider>
		</CrosswordSizeContext.Provider>
	);
}

CrosswordGrid.propTypes = CrosswordGridPropTypes;

CrosswordGrid.defaultProps = {
	theme: null,
};
